import React from 'react';
import { SingleSelectCombobox } from './ui/single-select-combobox';
import { MultiSelectCombobox } from './ui/multi-select-combobox';
import { ScribaneTimeline } from './ScribaneTimeline';
import type { Chapter } from './Chapters';

export interface ChaptersTimelineProps {
  chapters: Chapter[];
  chapterTimelineGroupBy: 'none' | 'character' | 'storyline' | 'scene';
  onChapterTimelineGroupByChange: (mode: 'none' | 'character' | 'storyline' | 'scene') => void;
  chapterTimelineGroupByCharacters: Set<string>;
  onChapterTimelineGroupByCharactersChange: (chars: Set<string>) => void;
  chapterTimelineGroupByStorylines: Set<string>;
  onChapterTimelineGroupByStorylinesChange: (storylines: Set<string>) => void;
  chapterTimelineGroupByScenes: Set<string>;
  onChapterTimelineGroupByScenesChange: (scenes: Set<string>) => void;
  entityRefs: Array<{ sourceType: string; sourceId: string; targetType: string; targetId: string }>;
  characters: Array<{ id: string; name: string; documentId?: string }>;
  storylines: Array<{ id: string; title: string }>;
  scenes: Array<{ id: string; title: string }>;
  onEditChapter: (chapter: Chapter) => void;
  t: (key: string) => string;
}

export const ChaptersTimeline: React.FC<ChaptersTimelineProps> = ({
  chapters,
  chapterTimelineGroupBy,
  onChapterTimelineGroupByChange,
  chapterTimelineGroupByCharacters,
  onChapterTimelineGroupByCharactersChange,
  chapterTimelineGroupByStorylines,
  onChapterTimelineGroupByStorylinesChange,
  chapterTimelineGroupByScenes,
  onChapterTimelineGroupByScenesChange,
  entityRefs,
  characters,
  storylines,
  scenes,
  onEditChapter,
  t,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
      <SingleSelectCombobox
        options={[
          { value: 'none', label: t('timeline_group_by_none') },
          { value: 'character', label: t('chapters_timeline_group_by_character') },
          { value: 'storyline', label: t('chapters_timeline_group_by_storyline') },
          { value: 'scene', label: t('chapters_timeline_group_by_scene') },
        ]}
        value={chapterTimelineGroupBy}
        onValueChange={(v) => onChapterTimelineGroupByChange(v as 'none' | 'character' | 'storyline' | 'scene')}
        placeholder={t('timeline_group_by')}
        searchPlaceholder={t('timeline_filter_search_placeholder')}
        emptyMessage={t('timeline_filter_no_results')}
        align="start"
      />
      {chapterTimelineGroupBy === 'character' && (
        <MultiSelectCombobox
          options={characters.map((c) => ({ value: c.id, label: c.name }))}
          selected={chapterTimelineGroupByCharacters}
          onSelectionChange={onChapterTimelineGroupByCharactersChange}
          label={t('chapters_timeline_group_by_characters_label')}
          searchPlaceholder={t('timeline_filter_search_placeholder')}
          emptyMessage={t('timeline_filter_no_results')}
          clearAllLabel={t('timeline_clear_all')}
          align="start"
        />
      )}
      {chapterTimelineGroupBy === 'storyline' && (
        <MultiSelectCombobox
          options={storylines.map((s) => ({ value: s.id, label: s.title }))}
          selected={chapterTimelineGroupByStorylines}
          onSelectionChange={onChapterTimelineGroupByStorylinesChange}
          label={t('chapters_timeline_group_by_storylines_label')}
          searchPlaceholder={t('timeline_filter_search_placeholder')}
          emptyMessage={t('timeline_filter_no_results')}
          clearAllLabel={t('timeline_clear_all')}
          align="start"
        />
      )}
      {chapterTimelineGroupBy === 'scene' && (
        <MultiSelectCombobox
          options={scenes.map((s) => ({ value: s.id, label: s.title }))}
          selected={chapterTimelineGroupByScenes}
          onSelectionChange={onChapterTimelineGroupByScenesChange}
          label={t('chapters_timeline_group_by_scenes_label')}
          searchPlaceholder={t('timeline_filter_search_placeholder')}
          emptyMessage={t('timeline_filter_no_results')}
          clearAllLabel={t('timeline_clear_all')}
          align="start"
        />
      )}
    </div>
    <ScribaneTimeline
      chapters={chapters}
      groupBy={chapterTimelineGroupBy}
      groupByCharacters={chapterTimelineGroupByCharacters}
      groupByStorylines={chapterTimelineGroupByStorylines}
      groupByScenes={chapterTimelineGroupByScenes}
      entityRefs={entityRefs}
      characters={characters}
      storylines={storylines}
      scenes={scenes}
      onChapterClick={(chapter) => {
        const full = chapters.find((c) => c.id === chapter.id);
        if (full) onEditChapter(full);
      }}
    />
  </div>
);
